import { useEffect, useState } from 'react';

/**
 * Animates a numeric value from `from` to `to` over `duration` ms using
 * requestAnimationFrame. Returns the current animated value (integer).
 *
 * Re-runs whenever `to` changes (e.g. data loads after mount).
 */
export function useAnimatedPct(
  to: number,
  { from = 0, duration = 2000 }: { from?: number; duration?: number } = {},
): number {
  const [animatedPct, setAnimatedPct] = useState(from);

  useEffect(() => {
    let startTs: number | null = null;
    let raf: number;

    const tick = (ts: number) => {
      if (!startTs) startTs = ts;
      const t = Math.min((ts - startTs) / duration, 1);
      // ease-out cubic: t starts fast and decelerates
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedPct(Math.round(from + eased * (to - from)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);

  return animatedPct;
}
